import { useEffect, useRef } from 'react';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from 'hooks';
import { selectAreCustomizationsReady, selectMerchantCustomizations } from 'containers/app/appSlice';
import type { MerchantFeeSettingsPayload } from 'containers/invoices/InvoicesSlice';
import type { MerchantFeeSettingsEditPayload } from 'containers/invoices/edit-invoice/invoicesEditSlice';

type FeeSettingsPayload = MerchantFeeSettingsPayload | MerchantFeeSettingsEditPayload;

export interface UseSyncFeeSettingsFromCustomizationsOptions {
  /** Page-specific gate (e.g. slice finished loading invoice/job/estimate data). */
  isReady: boolean;
  teamId: number | string | null | undefined;
}

/**
 * Dispatches the legacy fee-settings shape (surcharge + discount) from merchant customizations.
 * Runs once per team when customizations are ready and the page gate opens; resets when `isReady` or `teamId` changes.
 */
export const useSyncFeeSettingsFromCustomizations = (
  action: ActionCreatorWithPayload<FeeSettingsPayload>,
  { isReady, teamId }: UseSyncFeeSettingsFromCustomizationsOptions
) => {
  const dispatch = useAppDispatch();
  const customizations = useAppSelector(selectMerchantCustomizations);
  const customizationsReady = useAppSelector(selectAreCustomizationsReady);

  const syncedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    syncedKeyRef.current = null;
  }, [teamId]);

  useEffect(() => {
    if (!isReady) {
      syncedKeyRef.current = null;
    }
  }, [isReady]);

  useEffect(() => {
    if (!customizationsReady || !isReady || teamId === undefined || teamId === null || teamId === '') {
      return;
    }

    const key = `${teamId}`;
    if (syncedKeyRef.current === key) {
      return;
    }

    syncedKeyRef.current = key;
    dispatch(action(customizations));
  }, [action, customizations, customizationsReady, dispatch, isReady, teamId]);
};
